import { DealListItemResponse, updateDeal, UpdateDealRequest } from 'app/console-api';
import {
  BodyText,
  Button,
  FlexColumn,
  FlexRow,
  SvgIcon,
  Textarea
} from '@make-software/cspr-design';
import React, { useEffect, useState } from 'react';
import { RewardValueControl } from 'app/components/management/common/rewards/rewards-value-control';
import styled from 'styled-components';
import { HttpError } from 'app/components/utils/http-error';
import { AUTH_TOKEN, customErrorMessage, statusCode } from 'app/constants';
import { APPS_DEALS_VIEW_PATH, SIGN_IN_PATH } from 'app/route/paths';
import SuccessEntityWindow from 'app/components/common/success-entity-modal/success-entity-modal';
import ConfirmCancellation from 'assets/logos/confirm-cancellation.svg';
import CancellationSuccess from 'assets/logos/success-cancellation.svg';
import SuccessUpdatedIcon from 'assets/logos/success-entity.svg';
import { ErrorResult } from 'app/organizations-api/types';
import { ErrorWindow } from 'app/components/common/error-window/error-window';
import NotFoundIcon from 'assets/logos/no-found.svg';
import ErrorEntityWindow from 'app/components/common/error-entity-modal/error-entity-modal';
import { AppCategory } from 'app/console-api/apps/categories';
import {
  ButtonsContainer,
  StyledDismissButton,
} from 'app/components/common/common-styles';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { createUpdateDealValidationSchema } from 'app/components/management/apps/deals/common/deals-validation-schemas';
import { RewardsType, RewardsValue } from 'app/console-api/apps/types';
import { DealStatus } from 'app/components/common/deals-statuses/deals-statuses';
import { generatePath, useNavigate } from 'react-router-dom';
import { DealFormFields } from 'app/components/management/apps/deals/common/deal-form-fields';

interface DealViewSettingsPendingFormProps {
  data: DealListItemResponse;
  categoryIds: number[];
  appCategories: AppCategory[];
  rewardsTypes: RewardsType[];
}

export const StyledConfirmButton = styled(Button)(({ theme }) =>
  theme.withMedia({
    width: ['100%', '200px', '200px'],
    backgroundColor: theme.styleguideColors.fillPrimaryBlue,
    ':hover': {
      background: theme.styleguideColors.fillPrimaryBlueHover
    },
    ':active': {
      background: theme.styleguideColors.fillPrimaryBlueClick
    }
  })
);

const StyledSeparator = styled.div<{ margin?: string }>(({ theme, margin }) =>
  theme.withMedia({
    borderBottom: '1px solid ' + theme.styleguideColors.borderPrimary,
    margin: margin ? `${margin} 0` : '16px 0'
  })
);

const ButtonRow = styled(FlexRow)(({ theme }) =>
  theme.withMedia({
    marginTop: '40px'
  })
);

export const StyledRemoveText = styled.div(({ theme }) =>
  theme.withMedia({
    width: ['100%', '45%', '47%'],
    fontSize: '14px',
    fontWeight: '400',
    cursor: 'pointer',
    lineHeight: '20px',
    color: theme.styleguideColors.fillPrimaryRed,
    ':hover': {
      color: theme.styleguideColors.fillPrimaryRedHover
    },
    ':active': {
      color: theme.styleguideColors.fillPrimaryRedClick
    }
  })
);

export const DealEditFormGeneral = ({
  data,
  categoryIds,
  appCategories,
  rewardsTypes
}: DealViewSettingsPendingFormProps) => {
  const [openSuccessModal, setOpenSuccessModal] = useState<boolean>(false);
  const [openWarningModal, setOpenWarningModal] = useState<boolean>(false);
  const [openSuccessUpdateModal, setOpenSuccessUpdateModal] = useState<boolean>(false);
  const [unexpectedError, setUnexpectedError] = useState<ErrorResult | null>(null);
  const [openConflictErrorModal, setOpenConflictErrorModal] = useState<boolean>(false);
  const [conflictError, setConflictError] = useState<string>('');
  const navigate = useNavigate();

  const onDismiss = () => {
    navigate(generatePath(APPS_DEALS_VIEW_PATH, { appID: data.myApp.id, id: data.dealDetails.id }));
  };

  const [formData, setFormData] = useState({
    title: data.dealDetails.title,
    customMetadata: data.dealDetails.customMetadata
      ? JSON.stringify(data.dealDetails.customMetadata, null, 2)
      : '',
    rewards: data.rewards ? data.rewards : [{ rewardID: rewardsTypes[0].id, value: 100 }],
    categories: categoryIds
  });

  useEffect(() => {
    handleInputChange('categories', categoryIds);
  }, [categoryIds]);

  const {
    handleSubmit,
    setValue,
    clearErrors,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(createUpdateDealValidationSchema(data.dealDetails.tradeType)),
    defaultValues: formData
  });

  const handleOnCancel = () => {
    updateDeal(data.dealDetails.appID, data.dealDetails.id, {
      dealStatus: DealStatus.Cancelled,
      title: data.dealDetails.title,
      customMetadata: data.dealDetails.customMetadata,
      rewards: [],
      categories: []
    })
      .then((r) => setOpenSuccessModal(true))
      .catch((error: any) => {
        if (error instanceof HttpError) {
          switch (error.statusCode) {
            case statusCode.validation_error:
              setUnexpectedError({
                code: error.statusCode.toString(),
                message: error.message
              });
              break;
            case statusCode.conflict_error:
              setConflictError(error.info?.error.description || error.message);
              setOpenConflictErrorModal(true);
              break;
            case statusCode.authentication_error:
              localStorage.removeItem(AUTH_TOKEN);
              window.location.href = SIGN_IN_PATH;
              return;
            case statusCode.unexpected_error:
              setUnexpectedError(customErrorMessage);
              break;
            default:
              setUnexpectedError(customErrorMessage);
              break;
          }
        } else {
          setUnexpectedError(customErrorMessage);
        }
        console.error('Failed to cancel deals:', error);
      });
  };

  const handleInputChange = <K extends keyof typeof formData>(
    key: K,
    value: (typeof formData)[K]
  ) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value
    }));
    // @ts-ignore
    setValue(key, value);
    // @ts-ignore
    clearErrors(key);
  };

  const onSubmit = () => {
    handleSubmit(processSubmit)();
  };

  const processSubmit = () => {
    updateDeal(data.dealDetails.appID, data.dealDetails.id, {
      dealStatus: data.dealStatus,
      title: formData.title,
      customMetadata: formData.customMetadata ? JSON.parse(formData.customMetadata) : null,
      rewards: formData.rewards,
      categories: formData.categories
    })
      .then(() => {
        setOpenSuccessUpdateModal(true);
      })
      .catch((error: any) => {
        if (error instanceof HttpError) {
          switch (error.statusCode) {
            case statusCode.validation_error:
              setUnexpectedError({
                code: error.statusCode.toString(),
                message: error.message
              });
              break;
            case statusCode.conflict_error:
              setConflictError(error.info?.error.description || error.message);
              setOpenConflictErrorModal(true);
              break;
            case statusCode.authentication_error:
              localStorage.removeItem(AUTH_TOKEN);
              window.location.href = SIGN_IN_PATH;
              return;
            case statusCode.unexpected_error:
              setUnexpectedError(customErrorMessage);
              break;
            default:
              setUnexpectedError(customErrorMessage);
              break;
          }
        } else {
          setUnexpectedError(customErrorMessage);
        }
        console.error('Failed to update deals:', error);
      });
  };

  const handleCloseSuccessModal = () => {
    setOpenSuccessModal(false);
    navigate(generatePath(APPS_DEALS_VIEW_PATH, { appID: data.myApp.id, id: data.dealDetails.id }));
  };

  const handleOpenWarningModal = () => {
    setOpenWarningModal(true);
  };

  const handleCloseWarningModal = () => {
    setOpenWarningModal(false);
  };

  const handleCloseUpdateModal = () => {
    setOpenSuccessUpdateModal(false);
    navigate(generatePath(APPS_DEALS_VIEW_PATH, { appID: data.myApp.id, id: data.dealDetails.id }));
  };

  const handleCloseConflictErrorModal = () => {
    setOpenConflictErrorModal(false);
  };

  if (unexpectedError) {
    return (
      <ErrorWindow
        withModal
        title={unexpectedError.code}
        information={unexpectedError.message}
        icon={<SvgIcon src={NotFoundIcon} width={219} height={125} />}
      />
    );
  }

  return (
    <FlexColumn gap={32}>
      <SuccessEntityWindow
        dismissLabel={'Close'}
        onDismiss={handleCloseSuccessModal}
        isOpen={openSuccessModal}
        icon={<SvgIcon src={CancellationSuccess} width={195} height={120} />}
        title="Deal has been cancelled"
        information="The deal was successfully canceled. We will send a message to your partner."
      />
      <SuccessEntityWindow
        confirmColor={'red'}
        dismissLabel={'Close'}
        onConfirm={handleOnCancel}
        confirmLabel={'Confirm'}
        onDismiss={handleCloseWarningModal}
        isOpen={openWarningModal}
        icon={<SvgIcon src={ConfirmCancellation} width={195} height={120} />}
        title="Confirm deal cancellation"
        information="Once canceled, the deal will be in a final status and cannot be restored."
      />
      <SuccessEntityWindow
        dismissLabel={'Close'}
        onDismiss={handleCloseUpdateModal}
        isOpen={openSuccessUpdateModal}
        icon={<SvgIcon src={SuccessUpdatedIcon} width={195} height={120} />}
        title={'Deal updated'}
        information={'Deal has been updated successfully.'}
      />
      <ErrorEntityWindow
        dismissLabel={'Close'}
        onDismiss={handleCloseConflictErrorModal}
        isOpen={openConflictErrorModal}
        icon={<SvgIcon src={NotFoundIcon} width={195} height={120} />}
        title={conflictError}
        information={''}
      />

      <FlexColumn gap={8}>
        {data.dealStatus !== DealStatus.Pending && (
          <>
            <StyledSeparator margin={'5px'} />
            <StyledRemoveText onClick={handleOpenWarningModal}>Cancel Deal</StyledRemoveText>
            <StyledSeparator margin={'5px'} />
          </>
        )}

        {/* Configuration-driven form fields - includes title and all applicable fields */}
        <DealFormFields
          tradeType={data.dealDetails.tradeType}
          formData={{
            title: formData.title,
            customMetadata: formData.customMetadata,
            rewards: formData.rewards,
            categories: formData.categories
          }}
          errors={errors}
          offerData={[]}
          rewardsTypes={rewardsTypes}
          appCategories={{ data: appCategories, item_count: appCategories.length, page_count: 1 }}
          onFieldChange={(field, value) => handleInputChange(field as any, value)}
          isEditing={true}
        />
      </FlexColumn>
      <ButtonRow gap={40}>
        <ButtonsContainer justify={'space-between'} gap={16}>
          <StyledDismissButton color={'utility'} onClick={onDismiss}>
            Cancel
          </StyledDismissButton>
          <StyledConfirmButton onClick={onSubmit}>Save</StyledConfirmButton>
        </ButtonsContainer>
      </ButtonRow>
    </FlexColumn>
  );
};
